import React, { useCallback, useEffect, useState } from "react";
import { Card, TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { useNavigate } from "react-router-dom";
import { useInjection } from "@presentation/components/dependencyInjection";
import { SaveLicenseService } from "@application/useCases/license/SaveLicenseService";
import { TYPES } from "@utils/inversifyJS/types";
import { usePermissionStore } from "@presentation/stores/PermissionStore";
import { useNotifications } from "@presentation/components/notifications";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { invalidateLicensedSoftwareCache } from "@presentation/components/job/components/useLicensedSoftware";
import { DeploymentStorageConfigurationSection } from "@presentation/components/storageConfiguration/DeploymentStorageConfigurationSection";
import { SoftwareVersionConfigurationSection } from "@presentation/components/softwareVersions/SoftwareVersionConfigurationSection";
import type { SupportedSoftware } from "@domain/repositories/ISoftwareVersionsRepository";
import { useConfigurationTab } from "@presentation/hooks/useConfigurationTab";
import { useEntitlements } from "@presentation/hooks/useEntitlements";
import {
  fillCardStyle,
  mutedTextStyle,
  pageFillContentStyle,
  pageHeaderRegionStyle,
  pageHeaderStyle,
  pageScrollableContentStyle,
  pageShellStyle,
  pageTitleStyle,
  rowStyle,
} from "@presentation/common/pageLayout";

const ACTIVATION_POLL_INTERVAL_MS = 5000;
const ACTIVATION_TIMEOUT_MS = 5 * 60 * 1000;

const entitlementsTableStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 720,
  borderCollapse: "collapse",
  textAlign: "left",
  border: "1px solid #dbe2ea",
};
const entitlementHeaderCellStyle: React.CSSProperties = {
  padding: "10px 14px",
  background: "#f8fafc",
  borderBottom: "1px solid #dbe2ea",
  fontWeight: 700,
};
const entitlementCellStyle: React.CSSProperties = {
  padding: "10px 14px",
  borderBottom: "1px solid #e2e8f0",
};
const smallHelpTextStyle: React.CSSProperties = { margin: 0, color: "#64748b", fontSize: 12, lineHeight: 1.5 };

type ActivationPhase = "idle" | "activating" | "active" | "timedOut" | "failed";

const ACTIVATION_MESSAGES: Record<Exclude<ActivationPhase, "idle">, { text: string; color: string }> = {
  activating: {
    text: "Key submitted. The cluster is now running an activation job for it; this usually takes a minute or two. Keep this page open to follow the progress.",
    color: "#b45309",
  },
  active: {
    text: "Activated. The license is now present in the cluster and the engine can be selected in Job Setup.",
    color: "green",
  },
  timedOut: {
    text: "Still not activated after 5 minutes. The activation job reports no result when a key is rejected, so the key was most likely invalid or already in use. Check the key and try again, or contact support.",
    color: "#b00020",
  },
  failed: {
    text: "The key could not be submitted. Check your connection and try again.",
    color: "#b00020",
  },
};

const useActivationTracker = (saveLicenseService: SaveLicenseService, software: SupportedSoftware) => {
  const [phase, setPhase] = useState<ActivationPhase>("idle");
  const [startedAt, setStartedAt] = useState<number | null>(null);

  useEffect(() => {
    if (phase !== "activating" || startedAt === null) return;

    let isCancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const status = await saveLicenseService.getStatus();
        if (isCancelled) return;
        const licensed = software === "SpectroMine" ? status.spectroMineLicensed : status.spectronautLicensed;
        if (licensed && status.known) {
          invalidateLicensedSoftwareCache();
          setPhase("active");
          return;
        }
      } catch (error) {
        console.error("Could not read license status while activating", error);
      }
      if (isCancelled) return;
      if (Date.now() - startedAt >= ACTIVATION_TIMEOUT_MS) {
        setPhase("timedOut");
        return;
      }
      timer = setTimeout(poll, ACTIVATION_POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, ACTIVATION_POLL_INTERVAL_MS);
    return () => {
      isCancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase, startedAt, saveLicenseService, software]);

  return {
    phase,
    start: () => {
      setStartedAt(Date.now());
      setPhase("activating");
    },
    fail: () => setPhase("failed"),
  };
};

const ActivationMessage: React.FC<{ phase: ActivationPhase }> = ({ phase }) => {
  if (phase === "idle") return null;
  const { text, color } = ACTIVATION_MESSAGES[phase];
  return (
    <div role="status" style={{ color, marginTop: 8, fontSize: "0.9em", maxWidth: 520 }}>
      {text}
    </div>
  );
};

const getLicenseStatusLabel = (
  software: SupportedSoftware,
  status: { status: "loading" } | { status: "error" } | { status: "unknown" } | { status: "ready"; licensed: SupportedSoftware[] },
): string => {
  if (status.status === "loading") return "Checking...";
  if (status.status === "unknown") return "Waiting for cluster status";
  if (status.status === "error") return "Could not check license status";
  return status.licensed.includes(software) ? "Active" : "Inactive";
};

/**
Deployment-level configuration (`/settings?scope=deployment`) - Admin-only. License keys, deployment-wide storage
paths, and deployment-wide software-version allow-lists/defaults: the root every team/project
configuration falls back to. Team creation/deletion stays on the Teams page.
*/
const DeploymentSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const [selectedTab, setSelectedTab] = useConfigurationTab(["license", "entitlements", "storage", "software-versions", "teams"]);
  const [licenseKey, setLicenseKey] = useState("");
  const [spectroMineLicenseKey, setSpectroMineLicenseKey] = useState("");
  const [licenseEntryOpen, setLicenseEntryOpen] = useState<SupportedSoftware | null>(null);

  const saveLicenseService = useInjection<SaveLicenseService>(TYPES.SaveLicenseService);
  const spectronautActivation = useActivationTracker(saveLicenseService, "Spectronaut");
  const spectroMineActivation = useActivationTracker(saveLicenseService, "SpectroMine");
  const [licenseStatus, setLicenseStatus] = useState<{ status: "loading" } | { status: "error" } | { status: "unknown" } | { status: "ready"; licensed: SupportedSoftware[] }>({ status: "loading" });
  const { pushNotification } = useNotifications();
  const entitlements = useEntitlements();

  const handleSaveLicenseKey = async () => {
    try {
      await saveLicenseService.execute(licenseKey, "Spectronaut");
      // Activation is asynchronous, but drop the cached status so Job Setup re-reads it.
      invalidateLicensedSoftwareCache();
      spectronautActivation.start();
      setLicenseKey("");
    } catch (error) {
      spectronautActivation.fail();
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not save the Spectronaut license key.", "error", 10_000);
    }
  };

  const handleSaveSpectroMineLicenseKey = async () => {
    try {
      await saveLicenseService.execute(spectroMineLicenseKey, "SpectroMine");
      invalidateLicensedSoftwareCache();
      spectroMineActivation.start();
      setSpectroMineLicenseKey("");
    } catch (error) {
      spectroMineActivation.fail();
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not save the SpectroMine license key.", "error", 10_000);
    }
  };
  const refreshLicenseStatus = useCallback(async () => {
    setLicenseStatus({ status: "loading" });
    invalidateLicensedSoftwareCache();
    try {
      const status = await saveLicenseService.getStatus();
      if (!status.known) {
        setLicenseStatus({ status: "unknown" });
        return;
      }
      const licensed: SupportedSoftware[] = [];
      if (status.spectronautLicensed) licensed.push("Spectronaut");
      if (status.spectroMineLicensed) licensed.push("SpectroMine");
      setLicenseStatus({ status: "ready", licensed });
    } catch (error) {
      console.error("Could not refresh license status", error);
      setLicenseStatus({ status: "error" });
    }
  }, [saveLicenseService]);
  const licenseRows = [
    { software: "Spectronaut" as const, key: licenseKey, setKey: setLicenseKey, save: handleSaveLicenseKey, activation: spectronautActivation.phase },
    { software: "SpectroMine" as const, key: spectroMineLicenseKey, setKey: setSpectroMineLicenseKey, save: handleSaveSpectroMineLicenseKey, activation: spectroMineActivation.phase },
  ];
  useEffect(() => {
    void refreshLicenseStatus();
  }, [refreshLicenseStatus]);

  useEffect(() => {
    if (spectronautActivation.phase === "active" || spectroMineActivation.phase === "active") void refreshLicenseStatus();
  }, [refreshLicenseStatus, spectronautActivation.phase, spectroMineActivation.phase]);

  if (!isAdmin) {
    return (
      <div style={pageShellStyle}>
        <div style={pageHeaderRegionStyle}>
          <div style={pageHeaderStyle}>
            <h1 style={pageTitleStyle}>Deployment settings</h1>
          </div>
        </div>
        <div style={pageScrollableContentStyle}>
          <p style={mutedTextStyle}>Settings are only available to deployment admins.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>Deployment settings</h1>
        </div>
      </div>

      <div style={pageFillContentStyle}>
        <Card style={fillCardStyle}>
          <TabStrip className="scrollable-tabstrip" selected={selectedTab} onSelect={(event) => setSelectedTab(event.selected)}>
            <TabStripTab title="License">
              <div style={{ display: "grid", gap: 16, padding: "4px 4px 12px" }}>
                <div>
                  <Button type="button" onClick={() => void refreshLicenseStatus()} disabled={licenseStatus.status === "loading"}>
                    Refresh
                  </Button>
                </div>
                <table style={entitlementsTableStyle}>
                  <thead>
                    <tr>
                      <th scope="col" style={{ ...entitlementHeaderCellStyle, width: "40%" }}>
                        License
                      </th>
                      <th scope="col" style={{ ...entitlementHeaderCellStyle, width: "35%" }}>
                        Status
                      </th>
                      <th scope="col" style={{ ...entitlementHeaderCellStyle, width: "25%" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {licenseRows.map((row) => (
                      <React.Fragment key={row.software}>
                        <tr>
                          <td style={entitlementCellStyle}>{row.software}</td>
                          <td style={entitlementCellStyle}>{getLicenseStatusLabel(row.software, licenseStatus)}</td>
                          <td style={entitlementCellStyle}>
                            <Button type="button" fillMode="flat" onClick={() => setLicenseEntryOpen((current) => (current === row.software ? null : row.software))}>
                              {licenseEntryOpen === row.software ? "Cancel" : "Add key"}
                            </Button>
                          </td>
                        </tr>
                        {licenseEntryOpen === row.software && (
                          <tr>
                            <td colSpan={3} style={entitlementCellStyle}>
                              <div style={{ display: "grid", gap: 8, maxWidth: 520 }}>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <input
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder={`Paste your ${row.software} license key`}
                                    value={row.key}
                                    onChange={(event) => row.setKey(event.target.value)}
                                    style={{ flex: 1, minWidth: 0 }}
                                  />
                                  <Button type="button" onClick={() => void row.save()} disabled={!row.key.trim()}>
                                    Save
                                  </Button>
                                </div>
                                <ActivationMessage phase={row.activation} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <p style={smallHelpTextStyle}>Activation runs in the cluster and can take a moment; a search engine only appears in Job Setup once its license is present.</p>
              </div>
            </TabStripTab>

            <TabStripTab title="Entitlements">
              <div style={{ display: "grid", gap: 12, padding: "4px 4px 12px" }}>
                <div>
                  <Button type="button" onClick={entitlements.refresh} disabled={entitlements.isLoading}>
                    Refresh
                  </Button>
                </div>
                {entitlements.isLoading && <p role="status">Loading entitlements...</p>}
                {entitlements.error && <p role="alert">Could not load entitlements: {entitlements.error}</p>}
                {!entitlements.isLoading && !entitlements.error && entitlements.entitlements?.length === 0 && <p>No entitlements were reported.</p>}
                {!entitlements.isLoading && !entitlements.error && Boolean(entitlements.entitlements?.length) && (
                  <table style={entitlementsTableStyle}>
                    <thead>
                      <tr>
                        <th scope="col" style={{ ...entitlementHeaderCellStyle, width: "70%" }}>
                          Entitlement
                        </th>
                        <th scope="col" style={{ ...entitlementHeaderCellStyle, width: "30%" }}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {entitlements.entitlements?.map((entitlement) => (
                        <tr key={entitlement.name}>
                          <td style={entitlementCellStyle}>{entitlement.name}</td>
                          <td style={entitlementCellStyle}>{entitlement.isActive ? "Active" : "Inactive"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <p style={smallHelpTextStyle}>Contact the OmicsFlow team to change an entitlement.</p>
              </div>
            </TabStripTab>

            <TabStripTab title="Storage configuration">
              <div style={{ padding: "4px 4px 12px" }}>
                <DeploymentStorageConfigurationSection />
              </div>
            </TabStripTab>

            <TabStripTab title="Software versions">
              <div style={{ padding: "4px 4px 12px" }}>
                <SoftwareVersionConfigurationSection level="deployment" canManage={true} />
              </div>
            </TabStripTab>

            <TabStripTab title="Teams">
              <div style={{ display: "grid", gap: 10, padding: "4px 4px 12px" }}>
                <div style={rowStyle}>
                  <span>Create or delete teams, and manage their membership, from the Teams page.</span>
                  <Button type="button" onClick={() => navigate("/teams")}>
                    Go to Teams
                  </Button>
                </div>
              </div>
            </TabStripTab>
          </TabStrip>
        </Card>
      </div>
    </div>
  );
};

export default DeploymentSettingsPage;
